"use client";

import Image from "next/image";
import Link from "next/link";

import event_bg from "@/assets/img/update/bg/bg-gradient1-2.png";
import event_data, { type EventDataItem } from "@/data/EventData";
import { useLanguage } from "@/utils/LanguageContext";
import GetInvolvedButton from "@/component/common/GetInvolvedButton";

const parseEventDate = (input: string): Date | null => {
  try {
    const cleaned = input
      .replace(/\b(\d{1,2})(st|nd|rd|th)\b/i, "$1")
      .replace(/\s+/g, " ")
      .trim();
    const parsed = new Date(cleaned);

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  } catch {
    return null;
  }
};

const sortEvents = (
  items: EventDataItem[],
  direction: "asc" | "desc"
): EventDataItem[] =>
  [...items].sort((left, right) => {
    const leftMs = parseEventDate(left.date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
    const rightMs = parseEventDate(right.date)?.getTime() ?? Number.MAX_SAFE_INTEGER;

    return direction === "asc" ? leftMs - rightMs : rightMs - leftMs;
  });

const EventMeta = ({ item }: { item: EventDataItem }) => {
  const parsedDate = parseEventDate(item.date);

  return (
    <div className="event-meta event-meta-home">
      <span className="event-meta-item">
        <svg
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <path
            d="M7.99895 8.95321C9.1477 8.95321 10.0789 8.02197 10.0789 6.87321C10.0789 5.72446 9.1477 4.79321 7.99895 4.79321C6.85019 4.79321 5.91895 5.72446 5.91895 6.87321C5.91895 8.02197 6.85019 8.95321 7.99895 8.95321Z"
            stroke="var(--tg-primary-color)"
            strokeWidth="1.5"
          />
          <path
            d="M2.41281 5.65992C3.72615 -0.113413 12.2795 -0.106746 13.5861 5.66659C14.3528 9.05325 12.2461 11.9199 10.3995 13.6933C9.05948 14.9866 6.93948 14.9866 5.59281 13.6933C3.75281 11.9199 1.64615 9.04659 2.41281 5.65992Z"
            stroke="var(--tg-primary-color)"
            strokeWidth="1.5"
          />
        </svg>
        <span>{item.country}</span>
      </span>

      <span className="event-meta-item">
        <i className="far fa-clock" aria-hidden="true"></i>
        {parsedDate ? (
          <time dateTime={parsedDate.toISOString()}>{item.date}</time>
        ) : (
          <span>{item.date}</span>
        )}
      </span>
    </div>
  );
};

const EventCard = ({
  item,
  ctaLabel,
  showCta,
}: {
  item: EventDataItem;
  ctaLabel: string;
  showCta: boolean;
}) => {
  const titleContent = item.link ? (
    <Link href={item.link} target="_blank" rel="noopener noreferrer">
      {item.title}
    </Link>
  ) : (
    item.title
  );

  return (
    <article className="event-card event-card-home h-100">
      <div className="event-card-img">
        {item.link ? (
          <Link href={item.link} target="_blank" rel="noopener noreferrer">
            <Image src={item.thumb} alt={item.title} loading="lazy" />
          </Link>
        ) : (
          <Image src={item.thumb} alt={item.title} loading="lazy" />
        )}
      </div>

      <div className="event-card-content">
        <h3 className="event-card-title">{titleContent}</h3>
        <EventMeta item={item} />

        {showCta && item.link ? (
          <Link href={item.link} className="ts-buy-btn event-card-cta" target="_blank" rel="noopener noreferrer">
            {ctaLabel}
          </Link>
        ) : null}
      </div>
    </article>
  );
};

const EventArea = () => {
  const { t, lang } = useLanguage();
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const upcoming = sortEvents(
    event_data.filter((item) => {
      const parsedDate = parseEventDate(item.date);
      return parsedDate ? parsedDate >= today : true;
    }),
    "asc"
  );

  const past = sortEvents(
    event_data.filter((item) => {
      const parsedDate = parseEventDate(item.date);
      return parsedDate ? parsedDate < today : false;
    }),
    "desc"
  );

  return (
    <section
      className="pt-130 pb-140 overflow-hidden position-relative z-index-common"
      aria-labelledby="events-title"
    >
      <div className="bg-gradient-4">
        <Image src={event_bg} alt="" loading="lazy" />
      </div>

      <div className="event-area">
        <div className="container">
          <div className="section-title text-center mb-50">
            <h2 className="title style2" id="events-title">{t("Join Events for meet us")}</h2>
          </div>

          {upcoming.length > 0 ? (
            <div className="row gy-60 gx-30 justify-content-center">
              {upcoming.map((item) => (
                <div key={item.id} className="col-lg-4 col-md-6 d-flex">
                  <EventCard item={item} ctaLabel={t("JOIN US")} showCta />
                </div>
              ))}
            </div>
          ) : (
            <div className="event-empty-state">
              <p className="event-empty-copy">{t("Stay Tuned for Updates!")}</p>
              <Link href={`/${lang}/contact`} className="ts-buy-btn event-empty-cta">
                {t("Contact")}
              </Link>
            </div>
          )}

          {past.length > 0 && (
            <>
              <div className="section-title text-center mt-60 mb-30">
                <h3 className="title style2">{t("Past Events")}</h3>
              </div>
              <div className="row gy-60 gx-30 justify-content-center mt-10">
                {past.map((item) => (
                  <div key={`past-${item.id}`} className="col-lg-4 col-md-6 d-flex">
                    <EventCard item={item} ctaLabel={t("JOIN US")} showCta={false} />
                  </div>
                ))}
              </div>
            </>
          )}
          <GetInvolvedButton />
        </div>
      </div>

      <style jsx>{`
        .event-card-home {
          display: flex;
          flex-direction: column;
          width: 100%;
        }

        .event-card-home .event-card-content {
          display: flex;
          flex: 1 1 auto;
          flex-direction: column;
        }

        .event-card-home .event-card-title {
          min-height: 3.4rem;
        }

        .event-card-home .event-card-title a {
          color: inherit;
          text-decoration: none;
        }

        .event-card-home .event-card-title a:hover,
        .event-card-home .event-card-title a:focus-visible {
          color: var(--tg-primary-color);
        }

        .event-meta-home {
          display: grid;
          gap: 10px;
          margin-top: 1rem;
        }

        .event-meta-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          color: rgba(225, 238, 255, 0.82);
          line-height: 1.6;
        }

        .event-meta-item svg,
        .event-meta-item i {
          flex-shrink: 0;
        }

        .event-card-cta {
          margin-top: auto;
        }

        .event-empty-state {
          display: grid;
          justify-items: center;
          gap: 1rem;
          padding: clamp(1.5rem, 4vw, 2.5rem);
          border: 1px solid rgba(255, 255, 255, 0.12);
          border-radius: 28px;
          background:
            linear-gradient(160deg, rgba(9, 28, 53, 0.84), rgba(11, 35, 64, 0.62)),
            radial-gradient(760px 280px at 0% 0%, rgba(88, 170, 255, 0.12), transparent 58%);
          box-shadow:
            0 18px 42px rgba(3, 19, 43, 0.32),
            inset 0 1px 0 rgba(255, 255, 255, 0.05);
          text-align: center;
        }

        .event-empty-copy {
          margin: 0;
          color: rgba(225, 238, 255, 0.82);
          font-size: 1rem;
          line-height: 1.75;
        }

        .event-empty-cta {
          min-width: 180px;
        }
      `}</style>
    </section>
  );
};

export default EventArea;
